import {
  MessageId,
  type SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import type { SetVoiceAvailabilityResponse } from "@redotech/effect-api-shared/rpc/voice";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { REDO_RINGTONE } from "@redotech/redo-web/sound/sounds";
import { usePhoneWidget } from "@redotech/redo-web/voice/shared/phone-widget-context";
import { updateRingtoneVolume } from "@redotech/redo-web/voice/shared/ringtone-volume";
import { useRingtoneVolumeSync } from "@redotech/redo-web/voice/shared/use-ringtone-volume-sync";
import {
  getSavedOutputDeviceId,
  saveInputDeviceId,
  saveOutputDeviceId,
  setAudioOutputDevice,
} from "@redotech/redo-web/voice/shared/utils";
import { TelnyxRTC } from "@telnyx/webrtc";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  callsMatch,
  getUpdatedActiveCallState,
  isCurrentCallUpdate,
  isTerminalCallState,
  shouldClearIncomingCall,
  shouldRestoreWidgetForTerminalCall,
} from "./incoming-call-state";
import {
  useAddAgentToCall,
  useAddPhoneNumberToCall,
  useGetConferenceParticipants,
  useGetTelnyxTelephonyToken,
  useLookupCustomerByPhone,
  usePrepareOutboundCall,
  useSetCallHold,
  useSetCallRecording,
  useSetVoiceAvailability,
  type ConferenceParticipant,
} from "./use-voice-actions";
import {
  initialInboundAudioState,
  logVoiceAudioEvent,
  readInboundAudioSample,
  recordInboundAudioSample,
  summarizeInboundAudio,
  type InboundAudioState,
} from "./voice-audio-telemetry";
import {
  readVoiceDesiredOnline,
  writeVoiceDesiredOnline,
} from "./voice-availability-storage";
import {
  VOICE_CALL_CUSTOMER_EVENT,
  type VoiceCallCustomerDetail,
} from "./voice-call-customer-event";
import {
  VoiceContextV2,
  type ActiveCallV2,
  type AvailabilityStatus,
  type CallCustomer,
  type ConnectionStatus,
  type DebugTelnyxNotification,
  type IncomingCallV2,
  type MicPermission,
  type TelnyxCallLike,
  type VoiceV2State,
} from "./voice-context-v2";
import {
  describeReconnectAttempt,
  describeReconnectGiveUp,
  planReconnect,
  RECONNECT_DELAY_MS,
  type ConnectionDropCause,
} from "./voice-reconnect";

const HEARTBEAT_INTERVAL_MS = 20_000;
const MAX_DEBUG_TELNYX_NOTIFICATIONS = 12;

const getPeerConnection = (
  call: TelnyxCallLike & {
    readonly peer?: { readonly instance?: RTCPeerConnection };
    readonly rtcPeerConnection?: RTCPeerConnection;
  },
): RTCPeerConnection | null =>
  call.peer?.instance ?? call.rtcPeerConnection ?? null;

/** Per-call audio telemetry, reset when each call ends. */
interface CallAudioTracking {
  // The SDK can hand us a new object for the same call (e.g. a reattach).
  call: TelnyxCallLike;
  messageId: string | null;
  readonly isMonitor: boolean;
  readonly startedAtMs: number;
  inbound: InboundAudioState;
  everDeafened: boolean;
  everDeafenedForOwnPhone: boolean;
  playbackBlocked: boolean;
}

interface ProviderProps {
  readonly children: ReactNode;
}

/**
 * Owns the Telnyx WebRTC session, all voice RPCs, microphone permission
 * state, ringtone playback, and the active/incoming call state machine.
 * One provider instance == one Telnyx session, so the dev harness mounts
 * N providers (one per panel) to test multiple legs in one tab; the
 * production floating widget mounts exactly one.
 *
 * All effect-api calls go through `use-voice-actions.ts` — this file
 * does not call `effectApi.runPromise` directly.
 */
export const VoiceContextV2Provider = memo(function VoiceContextV2Provider({
  children,
}: ProviderProps) {
  // ── RPC handlers ──────────────────────────────────────────────────────
  const getTelnyxToken = useGetTelnyxTelephonyToken();
  const setVoiceAvailability = useSetVoiceAvailability();
  const prepareOutboundCall = usePrepareOutboundCall();
  const setCallHold = useSetCallHold();
  const setCallRecording = useSetCallRecording();
  const lookupCustomerByPhone = useLookupCustomerByPhone();
  const addAgentToCallRpc = useAddAgentToCall();
  const addPhoneNumberToCallRpc = useAddPhoneNumberToCall();
  const getConferenceParticipants = useGetConferenceParticipants();
  const {
    hide: hideWidget,
    isVisible: isWidgetVisible,
    show: showWidget,
  } = usePhoneWidget();

  // ── Local state ──────────────────────────────────────────────────────
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>("disconnected");
  const [desiredOnline, setDesiredOnline] = useState(false);
  const [statusDetail, setStatusDetail] = useState<string | null>(null);
  const [micPermission, setMicPermission] = useState<MicPermission>("unknown");
  const [micError, setMicError] = useState<string | null>(null);
  const [incoming, setIncoming] = useState<IncomingCallV2 | null>(null);
  const [activeCall, setActiveCall] = useState<ActiveCallV2 | null>(null);
  const [muted, setMuted] = useState(false);
  const [deafened, setDeafened] = useState(false);
  // Set when the agent's own forwarded phone joined and we deafened for it.
  const [autoDeafenedForOwnPhone, setAutoDeafenedForOwnPhone] = useState(false);
  const [onHold, setOnHold] = useState(false);
  const [playbackBlocked, setPlaybackBlocked] = useState(false);
  const [recording, setRecording] = useState(false);
  const [availability, setAvailability] = useState<AvailabilityStatus | null>(
    null,
  );
  const [targetMongoUserId, setTargetMongoUserId] = useState<
    MongoUserId | undefined
  >(undefined);
  const [remoteStream, setRemoteStream] = useState<MediaStream | undefined>(
    undefined,
  );
  const [participants, setParticipants] = useState<
    readonly ConferenceParticipant[]
  >([]);
  const [activeSupportTicketId, setActiveSupportTicketId] =
    useState<SupportTicketId | null>(null);
  const [dialerNumber, setDialerNumber] = useState("");
  const [widgetExpanded, setWidgetExpanded] = useState(false);
  const [debugTelnyxNotifications, setDebugTelnyxNotifications] = useState<
    readonly DebugTelnyxNotification[]
  >([]);
  // When the operator dials while offline we can't place the call
  // immediately — the Telnyx session has to come up first. Stashing
  // the number here lets a `useEffect` watching `connectionStatus`
  // fire the dial as soon as the session is ready.
  const [pendingOutboundNumber, setPendingOutboundNumber] = useState<
    string | null
  >(null);
  // True from the moment Call is clicked until the active-call UI is
  // up (or the dial fails). Lets the dialer button show "Calling…"
  // and stay disabled, so impatient operators don't double-click and
  // queue a second call.
  const [isPlacingOutbound, setIsPlacingOutbound] = useState(false);
  const [transferRinging, setTransferRinging] = useState<{
    readonly targetLabel: string;
  } | null>(null);
  const transferRingingClearTimerRef = useRef<number | null>(null);

  // The authenticated user; we compare their mongoId against the
  // `forwarded_phone` participant kind so the agent's *own* phone leg
  // toggles the local mute + deafen.
  const user = useRequiredContext(UserContext);

  // ── Refs ─────────────────────────────────────────────────────────────
  const telnyxRef = useRef<TelnyxRTC | null>(null);
  const callRef = useRef<TelnyxCallLike | null>(null);
  const incomingRef = useRef<IncomingCallV2 | null>(null);
  const statsPollRef = useRef<number | null>(null);
  const debugNotificationIdRef = useRef(0);
  // Tracks the messageId we've already auto-muted for so we don't keep
  // re-toggling on every poll, and so manual un-mute by the agent
  // sticks.
  const autoMutedForMessageIdRef = useRef<MessageId | null>(null);
  const isWidgetVisibleRef = useRef(isWidgetVisible);
  const widgetExpandedRef = useRef(widgetExpanded);
  const widgetStateBeforeIncomingRef = useRef<{
    readonly isVisible: boolean;
    readonly isExpanded: boolean;
  } | null>(null);

  // ── Auto-reconnect bookkeeping ───────────────────────────────────────
  // `wantOnlineRef` mirrors the operator's intent to be online so the
  // Telnyx event handlers (which capture state at session-creation time)
  // can read the latest value. `sessionGenRef` invalidates a dead
  // session's late events once we've moved on, and `lastHandledDropGenRef`
  // dedupes the error+socket-close pair Telnyx fires on a single failure
  // so one drop only burns one reconnect attempt.
  const wantOnlineRef = useRef(false);
  const sessionGenRef = useRef(0);
  const lastHandledDropGenRef = useRef(-1);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);

  useEffect(() => {
    isWidgetVisibleRef.current = isWidgetVisible;
  }, [isWidgetVisible]);

  useEffect(() => {
    widgetExpandedRef.current = widgetExpanded;
  }, [widgetExpanded]);

  const showIncomingCallWidget = useCallback(() => {
    widgetStateBeforeIncomingRef.current ??= {
      isVisible: isWidgetVisibleRef.current,
      isExpanded: widgetExpandedRef.current,
    };
    setWidgetExpanded(true);
    showWidget();
  }, [showWidget]);

  const restoreWidgetAfterIncomingCall = useCallback(() => {
    const previousState = widgetStateBeforeIncomingRef.current;
    if (previousState == null) {
      return;
    }
    widgetStateBeforeIncomingRef.current = null;
    setWidgetExpanded(previousState.isExpanded);
    if (!previousState.isVisible) {
      hideWidget();
    }
  }, [hideWidget]);

  // ── Microphone permission watcher ─────────────────────────────────────
  useEffect(() => {
    let permissionStatus: PermissionStatus | null = null;
    let cancelled = false;
    const onChange = () => {
      if (cancelled || !permissionStatus) {
        return;
      }
      setMicPermission(permissionStatus.state as MicPermission);
    };
    (async () => {
      try {
        permissionStatus = await navigator.permissions.query({
          name: "microphone" as PermissionName,
        });
        if (cancelled) {
          return;
        }
        setMicPermission(permissionStatus.state as MicPermission);
        permissionStatus.addEventListener("change", onChange);
      } catch {
        if (!cancelled) {
          setMicPermission("prompt");
        }
      }
    })();
    return () => {
      cancelled = true;
      permissionStatus?.removeEventListener("change", onChange);
    };
  }, []);

  const requestMic = useHandler(async () => {
    setMicError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      for (const track of stream.getTracks()) {
        track.stop();
      }
      setMicPermission("granted");
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const name = (err as { name?: string })?.name;
      if (name === "NotAllowedError") {
        setMicPermission("denied");
        setMicError(
          "Browser blocked microphone access. Open the site permissions and unblock the microphone, then reload.",
        );
      } else {
        setMicError(err instanceof Error ? err.message : String(err));
      }
    }
  });

  // ── Stats polling: detect bridged once we see inbound RTP ─────────────
  const stopStatsPolling = useCallback(() => {
    if (statsPollRef.current != null) {
      window.clearInterval(statsPollRef.current);
      statsPollRef.current = null;
    }
  }, []);

  const startStatsPolling = useCallback(
    (call: TelnyxCallLike) => {
      stopStatsPolling();
      const pc = getPeerConnection(call);
      if (pc == null) {
        return;
      }
      let lastBytes = 0;
      statsPollRef.current = window.setInterval(() => {
        if (callRef.current !== call) {
          stopStatsPolling();
          return;
        }
        void pc.getStats().then((report) => {
          for (const stat of report.values()) {
            if (
              stat.type === "inbound-rtp" &&
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              (stat as { kind?: string }).kind === "audio"
            ) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              const bytes = (stat as { bytesReceived?: number }).bytesReceived;
              if (typeof bytes === "number" && bytes > lastBytes) {
                if (lastBytes === 0 && bytes > 0) {
                  setActiveCall((cur) =>
                    cur
                      ? { ...cur, bridged: true, startedAt: new Date() }
                      : cur,
                  );
                  stopStatsPolling();
                  return;
                }
                lastBytes = bytes;
              }
            }
          }
        });
      }, 250);
    },
    [stopStatsPolling],
  );

  useEffect(() => stopStatsPolling, [stopStatsPolling]);

  // ── Availability publishing + heartbeat ───────────────────────────────
  /**
   * The server is the authority on whether an agent may be available for
   * voice. A refusal is not an error — the agent is simply not eligible right
   * now — so drop to offline and say why instead of showing a green Available
   * that will never ring.
   */
  const applyAvailabilityRefusal = useHandler(
    (result: SetVoiceAvailabilityResponse) => {
      if (result.accepted) {
        return;
      }
      setAvailability(null);
      setStatusDetail("Voice is not enabled for your account");
    },
  );

  const pushAvailability = useHandler(
    (status: "available" | "in-call" | "unavailable") => {
      setAvailability(status === "unavailable" ? null : status);
      setVoiceAvailability({ status, targetMongoUserId })
        .then(applyAvailabilityRefusal)
        .catch((err) => {
          console.error("setVoiceAvailability failed", { status, err });
        });
    },
  );

  useEffect(() => {
    if (availability == null) {
      return;
    }
    const id = window.setInterval(() => {
      setVoiceAvailability({ status: availability, targetMongoUserId })
        .then(applyAvailabilityRefusal)
        .catch((err) => {
          console.error("setVoiceAvailability heartbeat failed", {
            status: availability,
            err,
          });
        });
    }, HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [
    availability,
    targetMongoUserId,
    setVoiceAvailability,
    applyAvailabilityRefusal,
  ]);

  // ── Ringtone: play while a call is incoming or outbound is ringing ──
  useRingtoneVolumeSync();

  useEffect(() => {
    const outboundRinging =
      activeCall != null &&
      activeCall.direction === "outbound" &&
      activeCall.state === "ringing";
    if (incoming != null || outboundRinging) {
      updateRingtoneVolume();
      REDO_RINGTONE.play().catch((err) => {
        console.warn("Failed to play ringing sound:", err);
      });
    } else {
      REDO_RINGTONE.pause();
      REDO_RINGTONE.currentTime = 0;
    }
  }, [incoming, activeCall]);

  useEffect(
    () => () => {
      REDO_RINGTONE.pause();
      REDO_RINGTONE.currentTime = 0;
    },
    [],
  );

  // ── Customer lookup helper ────────────────────────────────────────────
  const lookupAndStashCustomer = useHandler(
    async (phoneNumber: string | null) => {
      if (!phoneNumber) {
        return;
      }
      try {
        const customer = await lookupCustomerByPhone({ phoneNumber });
        if (customer == null) {
          return;
        }
        const mappedCustomer: CallCustomer = {
          mongoCustomerId: customer.mongoCustomerId,
          name: customer.name,
          imageUrl: customer.imageUrl,
        };
        setActiveCall((cur) =>
          cur && cur.remoteNumber === phoneNumber
            ? { ...cur, customer: mappedCustomer }
            : cur,
        );
        setIncoming((cur) =>
          cur && cur.fromNumber === phoneNumber
            ? cur // incoming display reads fromNumber + customer separately if needed
            : cur,
        );
      } catch (err) {
        console.warn("Customer lookup failed:", err);
      }
    },
  );

  // ── Telnyx session lifecycle ──────────────────────────────────────────
  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current != null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  // A live session errored or its socket closed. If the operator still
  // wants to be online, transparently retry up to MAX_RECONNECT_ATTEMPTS
  // before giving up and dropping them to offline. Deduped per session
  // generation so the error→socket-close pair only burns one attempt; the
  // budget resets on a clean `telnyx.ready`.
  const handleConnectionDrop = useHandler(
    (gen: number, cause: ConnectionDropCause = "connection") => {
      if (lastHandledDropGenRef.current === gen) {
        return;
      }
      lastHandledDropGenRef.current = gen;
      if (!wantOnlineRef.current) {
        setConnectionStatus("disconnected");
        return;
      }
      const decision = planReconnect(reconnectAttemptsRef.current);
      if (decision.kind === "give-up") {
        // Persistent failure — stop retrying and clear the per-tab flag so a
        // refresh doesn't immediately loop back into the same failure.
        wantOnlineRef.current = false;
        setDesiredOnline(false);
        writeVoiceDesiredOnline(targetMongoUserId, false);
        setConnectionStatus("error");
        setStatusDetail(describeReconnectGiveUp(cause));
        return;
      }
      reconnectAttemptsRef.current = decision.attempt;
      setConnectionStatus("connecting");
      setStatusDetail(describeReconnectAttempt(cause, decision.attempt));
      clearReconnectTimer();
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        void establishSession();
      }, RECONNECT_DELAY_MS);
    },
  );

  const answeredCallWatchRef = useRef<WeakSet<TelnyxCallLike>>(new WeakSet());
  const callReachedMediaRef = useRef(false);

  // ── Audio devices ────────────────────────────────────────────────────
  // Owned here so the speaker choice applies to it and nothing rebinds it.
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const mutedRef = useRef(muted);
  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  const applyMicrophone = useHandler(
    async (call: TelnyxCallLike, deviceId: string): Promise<boolean> => {
      if (call.setAudioInDevice == null) {
        return false;
      }
      // Throws before swapping tracks if the device is gone.
      await call.setAudioInDevice(deviceId);
      // The replacement track starts enabled.
      if (mutedRef.current) {
        call.muteAudio?.();
      }
      return true;
    },
  );

  // ── Call audio telemetry ─────────────────────────────────────────────
  const callAudioRef = useRef<CallAudioTracking | null>(null);
  const audioMonitorRef = useRef<number | null>(null);
  const deafenedRef = useRef(deafened);
  const onHoldRef = useRef(onHold);
  useEffect(() => {
    deafenedRef.current = deafened;
    if (deafened && callAudioRef.current != null) {
      callAudioRef.current.everDeafened = true;
    }
  }, [deafened]);
  useEffect(() => {
    onHoldRef.current = onHold;
  }, [onHold]);
  // Outbound legs carry no message-id header; the active call has it.
  useEffect(() => {
    const tracking = callAudioRef.current;
    if (tracking != null && tracking.messageId == null && activeCall) {
      tracking.messageId = activeCall.messageId;
    }
  }, [activeCall]);

  const sampleCallAudio = useHandler(() => {
    const tracking = callAudioRef.current;
    const pc = tracking == null ? null : getPeerConnection(tracking.call);
    if (tracking == null || pc == null) {
      return;
    }
    pc.getStats()
      .then((report) => {
        const sample = readInboundAudioSample(report, Date.now());
        if (callAudioRef.current !== tracking || sample == null) {
          return;
        }
        const result = recordInboundAudioSample(tracking.inbound, sample);
        tracking.inbound = result.state;
        if (result.stallStarted) {
          logVoiceAudioEvent("voice.inbound_audio_stalled", {
            messageId: tracking.messageId,
            isMonitor: tracking.isMonitor,
            secondsIntoCall: Math.round(
              (sample.atMs - tracking.startedAtMs) / 1000,
            ),
            deafened: deafenedRef.current,
            muted: mutedRef.current,
            onHold: onHoldRef.current,
          });
        }
      })
      .catch(() => {
        // A closed peer connection rejects; the summary still reports.
      });
  });

  const startCallAudioTelemetry = useHandler(
    (
      call: TelnyxCallLike,
      info: { readonly messageId: string | null; readonly isMonitor: boolean },
    ) => {
      const current = callAudioRef.current;
      if (current != null && callsMatch(current.call, call)) {
        current.call = call;
        return;
      }
      if (current != null) {
        finishCallAudioTelemetry();
      }
      callAudioRef.current = {
        call,
        messageId: info.messageId,
        isMonitor: info.isMonitor,
        startedAtMs: Date.now(),
        inbound: initialInboundAudioState,
        everDeafened: deafenedRef.current,
        everDeafenedForOwnPhone: false,
        playbackBlocked: false,
      };
      audioMonitorRef.current = window.setInterval(sampleCallAudio, 1000);
    },
  );

  const finishCallAudioTelemetry = useHandler(() => {
    if (audioMonitorRef.current != null) {
      window.clearInterval(audioMonitorRef.current);
      audioMonitorRef.current = null;
    }
    const tracking = callAudioRef.current;
    if (tracking == null) {
      return;
    }
    callAudioRef.current = null;
    logVoiceAudioEvent("voice.call_audio_summary", {
      messageId: tracking.messageId,
      isMonitor: tracking.isMonitor,
      direction: activeCall?.direction ?? null,
      callSeconds: Math.round((Date.now() - tracking.startedAtMs) / 1000),
      ...summarizeInboundAudio(tracking.inbound),
      everDeafened: tracking.everDeafened,
      everDeafenedForOwnPhone: tracking.everDeafenedForOwnPhone,
      deafenedAtEnd: deafenedRef.current,
      mutedAtEnd: mutedRef.current,
      playbackBlocked: tracking.playbackBlocked,
      savedSpeaker: getSavedOutputDeviceId() != null,
    });
  });

  useEffect(() => finishCallAudioTelemetry, [finishCallAudioTelemetry]);

  // Autoplay can be refused silently, so start playback ourselves.
  const playRemoteAudio = useHandler((element: HTMLAudioElement) => {
    element
      .play()
      .then(() => setPlaybackBlocked(false))
      .catch((err: unknown) => {
        const errorName = err instanceof Error ? err.name : "unknown";
        // AbortError just means the stream was replaced mid-play.
        if (errorName === "AbortError") {
          return;
        }
        const tracking = callAudioRef.current;
        const context = {
          messageId: tracking?.messageId ?? null,
          isMonitor: tracking?.isMonitor ?? false,
        };
        if (errorName !== "NotAllowedError") {
          logVoiceAudioEvent("voice.playback_failed", {
            ...context,
            errorName,
          });
          return;
        }
        setPlaybackBlocked(true);
        if (tracking != null) {
          tracking.playbackBlocked = true;
        }
        logVoiceAudioEvent("voice.playback_blocked", context);
      });
  });

  const resumePlayback = useHandler(() => {
    const element = remoteAudioRef.current;
    if (element != null) {
      playRemoteAudio(element);
    }
  });

  useEffect(() => {
    const element = remoteAudioRef.current;
    if (element == null || remoteStream == null) {
      return;
    }
    element.srcObject = remoteStream;
    const speakerId = getSavedOutputDeviceId();
    if (speakerId != null) {
      setAudioOutputDevice(element, speakerId).catch((err) => {
        console.warn("Saved speaker unavailable; using the default", err);
      });
    }
    playRemoteAudio(element);
  }, [remoteStream, playRemoteAudio]);

  const endActiveCall = useHandler((detail: string | null) => {
    finishCallAudioTelemetry();
    callReachedMediaRef.current = false;
    callRef.current = null;
    setActiveCall(null);
    setMuted(false);
    setDeafened(false);
    setAutoDeafenedForOwnPhone(false);
    setPlaybackBlocked(false);
    setOnHold(false);
    setRecording(false);
    setRemoteStream(undefined);
    stopStatsPolling();
    if (detail != null) {
      setStatusDetail(detail);
    }
    if (incomingRef.current == null) {
      restoreWidgetAfterIncomingCall();
    }
    if (telnyxRef.current != null && wantOnlineRef.current) {
      pushAvailability("available");
    }
  });

  const watchAnswerConnection = useHandler((call: TelnyxCallLike) => {
    if (answeredCallWatchRef.current.has(call)) {
      return;
    }
    const pc = getPeerConnection(call);
    if (pc == null) {
      return;
    }
    answeredCallWatchRef.current.add(call);
    const onConnectionState = () => {
      if (
        pc.connectionState !== "failed" ||
        callRef.current !== call ||
        callReachedMediaRef.current
      ) {
        return;
      }
      endActiveCall("Call disconnected before it connected");
    };
    pc.addEventListener("connectionstatechange", onConnectionState);
    onConnectionState();
  });

  // Brings up a fresh Telnyx session. Shared by the user-facing `connect`
  // action and the auto-reconnect timer; neither resets the attempt budget
  // here (only a clean `telnyx.ready` or a manual `connect` does) so the cap
  // holds across a burst of failures.
  const establishSession = useHandler(
    async (options?: { readonly reportAvailable?: boolean }) => {
      const reportAvailable = options?.reportAvailable ?? true;
      const gen = ++sessionGenRef.current;
      // Drop any prior session before standing up a new one so a reconnect
      // doesn't leave two sockets registered.
      const existingSession = telnyxRef.current;
      telnyxRef.current = null;
      if (existingSession) {
        try {
          void existingSession.disconnect();
        } catch {
          // best-effort teardown
        }
      }
      // Pre-warm the ringtone from this user-gesture context so Chrome's
      // autoplay policy allows the programmatic play() when a call arrives.
      REDO_RINGTONE.play()
        .then(() => {
          REDO_RINGTONE.pause();
          REDO_RINGTONE.currentTime = 0;
        })
        .catch(() => {
          // Ignore — the element may already be playing, or the browser may
          // still block it; the incoming-call effect will try again and warn.
        });
      let dropCause: ConnectionDropCause = "sign-in";
      try {
        setConnectionStatus("fetching-token");
        setStatusDetail(null);
        setDebugTelnyxNotifications([]);
        const token = await getTelnyxToken({ targetMongoUserId });
        if (sessionGenRef.current !== gen) {
          return;
        }

        dropCause = "connection";
        setConnectionStatus("connecting");
        const session = new TelnyxRTC({ login_token: token });
        session.on("telnyx.ready", () => {
          if (sessionGenRef.current !== gen) {
            return;
          }
          // Clean connection — refill the reconnect budget.
          reconnectAttemptsRef.current = 0;
          setConnectionStatus("ready");
          setStatusDetail(null);
          if (reportAvailable) {
            pushAvailability("available");
          }
        });
        session.on("telnyx.error", (err: unknown) => {
          if (sessionGenRef.current !== gen) {
            return;
          }
          setStatusDetail(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            String((err as { message?: string })?.message ?? err),
          );
          if (reportAvailable) {
            pushAvailability("unavailable");
          }
          handleConnectionDrop(gen);
        });
        session.on("telnyx.socket.close", () => {
          if (sessionGenRef.current !== gen) {
            return;
          }
          if (reportAvailable) {
            pushAvailability("unavailable");
          }
          handleConnectionDrop(gen);
        });

        session.on("telnyx.notification", (notification: unknown) => {
          if (sessionGenRef.current !== gen) {
            return;
          }
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const n = notification as {
            type?: string;
            call?: TelnyxCallLike & {
              state?: string;
              id?: string;
              callID?: string;
              callId?: string;
              options?: {
                customHeaders?: readonly { name: string; value: string }[];
                remoteCallerNumber?: string;
                remoteCallerName?: string;
              };
            };
          };
          const headers = n.call?.options?.customHeaders ?? [];
          const messageIdRaw =
            headers.find((h) => h.name === "X-Redo-Message-Id")?.value ?? null;
          const fromNumber =
            headers.find((h) => h.name === "X-Redo-From-Number")?.value ??
            n.call?.options?.remoteCallerNumber ??
            n.call?.options?.remoteCallerName ??
            null;
          setDebugTelnyxNotifications((prev) =>
            [
              {
                id: ++debugNotificationIdRef.current,
                receivedAt: new Date(),
                type: n.type ?? "unknown",
                callState: n.call?.state ?? null,
                callId: n.call?.callID ?? n.call?.callId ?? n.call?.id ?? null,
                fromNumber,
                messageId: messageIdRaw,
                isDebugPing: headers.some(
                  (h) => h.name === "X-Redo-Voice-Debug-Ping",
                ),
                customHeaders: headers,
              },
              ...prev,
            ].slice(0, MAX_DEBUG_TELNYX_NOTIFICATIONS),
          );
          if (n?.type !== "callUpdate" || !n.call?.state) {
            return;
          }
          const call = n.call;
          const state = call.state;
          if (state == null) {
            return;
          }
          const isActiveCallUpdate = isCurrentCallUpdate(callRef.current, call);

          if (state === "ringing" && !isActiveCallUpdate) {
            let messageId: MessageId | null = null;
            if (messageIdRaw) {
              try {
                messageId = MessageId.make(messageIdRaw);
              } catch (err) {
                console.warn(
                  "Invalid X-Redo-Message-Id header, hold/record disabled for this call",
                  { messageIdRaw, err },
                );
              }
            }
            // Already opted in via the dashboard's Join button — answer now.
            const isManagerMonitor = headers.some(
              (h) => h.name === "X-Redo-Manager-Monitor",
            );
            if (isManagerMonitor) {
              const managerVisibility =
                headers.find((h) => h.name === "X-Redo-Manager-Visibility")
                  ?.value === "private"
                  ? "private"
                  : "visible";
              try {
                call.answer();
                callRef.current = call;
                setActiveCall({
                  messageId,
                  direction: "inbound",
                  remoteNumber: fromNumber ?? "unknown",
                  customer: null,
                  state: "answering",
                  startedAt: null,
                  bridged: false,
                  managerVisibility,
                });
                showIncomingCallWidget();
                // Join muted; private can never unmute (see toggleMute).
                call.toggleAudioMute?.();
                setMuted(true);
              } catch (err) {
                setStatusDetail(
                  err instanceof Error ? err.message : String(err),
                );
              }
              if (fromNumber) {
                void lookupAndStashCustomer(fromNumber);
              }
              return;
            }
            const nextIncoming = { messageId, fromNumber, call };
            incomingRef.current = nextIncoming;
            setIncoming(nextIncoming);
            showIncomingCallWidget();
            if (fromNumber) {
              void lookupAndStashCustomer(fromNumber);
            }
            return;
          }

          const presentedIncoming = incomingRef.current?.call ?? null;
          if (
            presentedIncoming != null &&
            shouldClearIncomingCall(presentedIncoming, call, state)
          ) {
            incomingRef.current = null;
            setIncoming(null);
          }

          if (isActiveCallUpdate) {
            if (state === "active") {
              callReachedMediaRef.current = true;
            }
            watchAnswerConnection(call);
            setActiveCall((cur) =>
              cur
                ? { ...cur, state: getUpdatedActiveCallState(cur, state) }
                : cur,
            );
            if (state === "active") {
              const isMonitor = headers.some(
                (h) => h.name === "X-Redo-Manager-Monitor",
              );
              // Whisper-role audio isn't reliable inbound RTP — bridge on "active" instead.
              if (isMonitor) {
                setActiveCall((cur) =>
                  cur ? { ...cur, bridged: true, startedAt: new Date() } : cur,
                );
              } else {
                startStatsPolling(call);
              }
              if (reportAvailable) {
                pushAvailability("in-call");
              }
              startCallAudioTelemetry(call, {
                messageId: messageIdRaw,
                isMonitor,
              });
              setRemoteStream(call.remoteStream);
            }
          }

          if (isTerminalCallState(state) && isActiveCallUpdate) {
            endActiveCall(null);
          } else if (
            shouldRestoreWidgetForTerminalCall(
              presentedIncoming,
              null,
              call,
              state,
            )
          ) {
            restoreWidgetAfterIncomingCall();
          }
        });

        await session.connect();
        if (sessionGenRef.current !== gen) {
          // Superseded mid-connect (e.g. operator went offline). Tear down.
          try {
            void session.disconnect();
          } catch {
            // best-effort teardown
          }
          return;
        }
        telnyxRef.current = session;
      } catch (err) {
        if (sessionGenRef.current !== gen) {
          return;
        }
        setStatusDetail(err instanceof Error ? err.message : String(err));
        if (reportAvailable) {
          pushAvailability("unavailable");
        }
        handleConnectionDrop(gen, dropCause);
      }
    },
  );

  // User-facing "go online". A manual Available click always gets a fresh
  // set of reconnect attempts.
  const connect = useHandler(async () => {
    wantOnlineRef.current = true;
    reconnectAttemptsRef.current = 0;
    clearReconnectTimer();
    setDesiredOnline(true);
    // Remember, per-tab, that this operator wants to be online so a page
    // refresh restores availability instead of dropping them to offline.
    writeVoiceDesiredOnline(targetMongoUserId, true);
    await establishSession();
  });

  /** Silent connect for monitoring a call — never reports "available". */
  const connectForMonitoring = useHandler(async () => {
    if (telnyxRef.current != null) {
      return;
    }
    reconnectAttemptsRef.current = 0;
    clearReconnectTimer();
    await establishSession({ reportAvailable: false });
  });

  const disconnect = useHandler(async () => {
    wantOnlineRef.current = false;
    reconnectAttemptsRef.current = 0;
    clearReconnectTimer();
    // Invalidate the current session so the socket-close fired by the
    // disconnect below isn't mistaken for a failure and reconnected.
    sessionGenRef.current += 1;
    setDesiredOnline(false);
    writeVoiceDesiredOnline(targetMongoUserId, false);
    const session = telnyxRef.current;
    telnyxRef.current = null;
    finishCallAudioTelemetry();
    callRef.current = null;
    incomingRef.current = null;
    setActiveCall(null);
    setIncoming(null);
    setMuted(false);
    setDeafened(false);
    setAutoDeafenedForOwnPhone(false);
    setPlaybackBlocked(false);
    setOnHold(false);
    setRecording(false);
    setRemoteStream(undefined);
    setAvailability(null);
    setDebugTelnyxNotifications([]);
    stopStatsPolling();
    if (session) {
      try {
        await session.disconnect();
      } catch {
        // Ignore disconnect errors on teardown.
      }
    }
    setVoiceAvailability({ status: "unavailable", targetMongoUserId }).catch(
      (err) => {
        console.error("setVoiceAvailability(unavailable) failed", err);
      },
    );
    setConnectionStatus("disconnected");
    setStatusDetail(null);
  });

  // Tear down session on unmount so panels don't leak availability rows.
  useEffect(() => {
    return () => {
      // Invalidate the session and cancel any pending reconnect so a timer
      // doesn't fire establishSession after the provider is gone.
      sessionGenRef.current += 1;
      if (reconnectTimerRef.current != null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      const session = telnyxRef.current;
      telnyxRef.current = null;
      if (session) {
        try {
          void session.disconnect();
        } catch {
          // best-effort
        }
      }
    };
  }, []);

  // Restore per-tab availability after a refresh: if this tab had the
  // operator online before the reload, bring the Telnyx session back up
  // automatically so they don't have to re-toggle Available every refresh.
  useEffect(() => {
    if (readVoiceDesiredOnline(targetMongoUserId)) {
      void connect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally mount-only — identity changes have their own teardown flow

  // Switching the acting user requires a fresh telephony token. Tear
  // the session down — operator can reconnect via the status dropdown.
  const handleSetTargetMongoUserId = useHandler(
    (id: MongoUserId | undefined) => {
      if (id === targetMongoUserId) {
        return;
      }
      if (telnyxRef.current != null) {
        void disconnect();
      }
      setTargetMongoUserId(id);
    },
  );

  // ── Call actions ─────────────────────────────────────────────────────
  const placeOutbound = useHandler(async (phoneNumber: string) => {
    const trimmed = phoneNumber.trim();
    if (!trimmed) {
      return;
    }
    setIsPlacingOutbound(true);
    const session = telnyxRef.current;
    if (!session) {
      // Operator is offline — flip them to Available, queue the dial,
      // and the connection-watching useEffect fires it once the
      // session is ready. Outbound shouldn't require a manual
      // "go online" step first.
      setPendingOutboundNumber(trimmed);
      void connect();
      return;
    }
    try {
      const resp = await prepareOutboundCall({ customerPhoneNumber: trimmed });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const telnyxCall = session.newCall({
        destinationNumber: resp.toPhoneNumber,
        callerNumber: resp.fromPhoneNumber,
        audio: true,
        video: false,
        clientState: resp.clientState,
      }) as unknown as TelnyxCallLike;
      callRef.current = telnyxCall;
      setActiveCall({
        messageId: resp.messageId,
        direction: "outbound",
        remoteNumber: resp.toPhoneNumber,
        customer: null,
        state: "ringing",
        startedAt: null,
        bridged: false,
        managerVisibility: null,
      });
      void lookupAndStashCustomer(resp.toPhoneNumber);
    } catch (err) {
      setStatusDetail(err instanceof Error ? err.message : String(err));
      setIsPlacingOutbound(false);
    }
  });

  // Once an active call is established (or any prior call ends), the
  // dial flow is over — drop the "Calling…" lock so the dialer button
  // re-enables for the next call.
  useEffect(() => {
    if (activeCall != null) {
      setIsPlacingOutbound(false);
    }
  }, [activeCall]);

  // Fire any queued outbound dial as soon as the Telnyx session
  // becomes ready. `placeOutbound` queues a number when called while
  // offline; this effect drains the queue once `connect()` finishes.
  useEffect(() => {
    if (connectionStatus !== "ready") {
      return;
    }
    if (pendingOutboundNumber == null) {
      return;
    }
    const number = pendingOutboundNumber;
    setPendingOutboundNumber(null);
    void placeOutbound(number);
  }, [connectionStatus, pendingOutboundNumber, placeOutbound]);

  // Subscribe to "call this customer" intents fired from outside the
  // widget tree (e.g., the support ticket voice thread's call-back
  // button). Pop the widget open and prefill the dialer — the
  // operator hits Call themselves. We don't auto-dial because the
  // operator may want to verify the number, change identity, or just
  // not place the call after all.
  useEffect(() => {
    const handler = (e: Event) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const detail = (e as CustomEvent<VoiceCallCustomerDetail>).detail;
      if (!detail?.phoneNumber) {
        return;
      }
      setDialerNumber(detail.phoneNumber);
      setWidgetExpanded(true);
      showWidget();
    };
    window.addEventListener(VOICE_CALL_CUSTOMER_EVENT, handler);
    return () => window.removeEventListener(VOICE_CALL_CUSTOMER_EVENT, handler);
  }, [showWidget]);

  const acceptIncoming = useHandler(() => {
    const current = incomingRef.current ?? incoming;
    if (!current) {
      return;
    }
    incomingRef.current = null;
    setIncoming(null);
    try {
      current.call.answer();
    } catch (err) {
      setStatusDetail(err instanceof Error ? err.message : String(err));
      restoreWidgetAfterIncomingCall();
      return;
    }
    if (isTerminalCallState(current.call.state ?? "")) {
      setStatusDetail("The call ended before it connected");
      restoreWidgetAfterIncomingCall();
      return;
    }
    callReachedMediaRef.current = false;
    callRef.current = current.call;
    setActiveCall({
      messageId: current.messageId,
      direction: "inbound",
      remoteNumber: current.fromNumber ?? "unknown",
      customer: null,
      state: "answering",
      startedAt: null,
      bridged: false,
      managerVisibility: null,
    });
    watchAnswerConnection(current.call);
    if (current.fromNumber) {
      void lookupAndStashCustomer(current.fromNumber);
    }
  });

  const rejectIncoming = useHandler(() => {
    const current = incoming;
    if (current) {
      try {
        current.call.hangup();
      } catch {
        // SDK may have already torn it down.
      }
    }
    incomingRef.current = null;
    setIncoming(null);
    restoreWidgetAfterIncomingCall();
  });

  const hangup = useHandler(() => {
    finishCallAudioTelemetry();
    const call = callRef.current;
    if (call) {
      try {
        call.hangup();
      } catch {
        // best-effort
      }
    }
    callRef.current = null;
    setActiveCall(null);
    setMuted(false);
    setDeafened(false);
    setAutoDeafenedForOwnPhone(false);
    setPlaybackBlocked(false);
    setOnHold(false);
    setRecording(false);
    setRemoteStream(undefined);
    stopStatsPolling();
  });

  // ── Call modifiers ───────────────────────────────────────────────────
  const toggleMute = useHandler(() => {
    const call = callRef.current;
    if (!call) {
      return;
    }
    if (activeCall?.managerVisibility === "private") {
      return;
    }
    try {
      call.toggleAudioMute?.();
      setMuted((m) => !m);
    } catch (err) {
      console.error("toggleMute failed", err);
    }
  });

  const toggleDeafen = useHandler(() => {
    const call = callRef.current;
    if (!call) {
      return;
    }
    try {
      call.toggleDeaf?.();
      setDeafened((d) => !d);
      setAutoDeafenedForOwnPhone(false);
    } catch (err) {
      console.error("toggleDeafen failed", err);
    }
  });

  // Hold and recording optimistically flip local state, then call
  // effect-api. Revert on error so the UI reflects reality.
  const toggleHold = useHandler(() => {
    const messageId = activeCall?.messageId;
    if (messageId == null) {
      return;
    }
    const next = !onHold;
    setOnHold(next);
    setCallHold({ messageId, onHold: next }).catch((err) => {
      console.error("setCallHold failed", err);
      setOnHold((cur) => !cur);
    });
  });

  const toggleRecord = useHandler(() => {
    const messageId = activeCall?.messageId;
    if (messageId == null) {
      return;
    }
    const next = !recording;
    setRecording(next);
    setCallRecording({ messageId, recording: next }).catch((err) => {
      console.error("setCallRecording failed", err);
      setRecording((cur) => !cur);
    });
  });

  const sendDtmf = useHandler((digit: string) => {
    const call = callRef.current;
    if (!call) {
      return;
    }
    try {
      call.dtmf?.(digit);
    } catch (err) {
      console.error("sendDtmf failed", err);
    }
  });

  const selectMicrophone = useHandler(
    async (deviceId: string): Promise<boolean> => {
      const call = callRef.current;
      try {
        if (call != null && !(await applyMicrophone(call, deviceId))) {
          return false;
        }
      } catch (err) {
        console.error("selectMicrophone failed", err);
        return false;
      }
      saveInputDeviceId(deviceId);
      return true;
    },
  );

  const selectSpeaker = useHandler(
    async (deviceId: string): Promise<boolean> => {
      const element = remoteAudioRef.current;
      try {
        if (
          element != null &&
          !(await setAudioOutputDevice(element, deviceId))
        ) {
          return false;
        }
      } catch (err) {
        console.error("selectSpeaker failed", err);
        return false;
      }
      saveOutputDeviceId(deviceId);
      return true;
    },
  );

  // ── Conference participants polling ──────────────────────────────────
  // Poll every 3s while a call is active so warm-transfer joins/leaves
  // surface in the UI. Empty list whenever there's no active call.
  const activeMessageId = activeCall?.messageId ?? null;
  useEffect(() => {
    if (activeMessageId == null) {
      setParticipants([]);
      setActiveSupportTicketId(null);
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await getConferenceParticipants({
          messageId: activeMessageId,
        });
        if (!cancelled) {
          setParticipants(next.participants);
          setActiveSupportTicketId(next.supportTicketId);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("getConferenceParticipants failed", err);
        }
      }
    };
    void tick();
    const interval = window.setInterval(() => {
      void tick();
    }, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [activeMessageId, getConferenceParticipants]);

  // ── Auto-mute + deafen on forwarded-phone connect ───────────────────
  // When the agent has a saved `forwardingPhoneNumber` and the dialed
  // phone leg joins the conference, mute + deafen the WebRTC browser
  // session so the agent only hears audio on their phone. Tracked by
  // messageId so a manual un-mute doesn't get reverted on the next
  // poll.
  //
  // `MongoUserId` is a prefixed branded form (`m_us_<hex>`); `user._id`
  // is the raw 24-char hex from Mongo. Convert the latter via
  // `MongoUserId.fromString` so the equality check works.
  const ownMongoId = useMemo<MongoUserId | null>(() => {
    if (targetMongoUserId !== undefined) {
      return targetMongoUserId;
    }
    if (!user?._id) {
      return null;
    }
    try {
      return MongoUserId.fromString(user._id);
    } catch {
      return null;
    }
  }, [targetMongoUserId, user]);

  const ownPhoneInCall = useMemo(
    () =>
      ownMongoId != null &&
      participants.some(
        (p) => p.kind === "forwarded_phone" && p.mongoUserId === ownMongoId,
      ),
    [participants, ownMongoId],
  );
  // Only while that phone is still on the call; otherwise the agent
  // really can't hear the caller.
  const deafenedForOwnPhone = autoDeafenedForOwnPhone && ownPhoneInCall;
  useEffect(() => {
    if (deafenedForOwnPhone && callAudioRef.current != null) {
      callAudioRef.current.everDeafenedForOwnPhone = true;
    }
  }, [deafenedForOwnPhone]);

  useEffect(() => {
    const messageId = activeCall?.messageId;
    if (messageId == null) {
      autoMutedForMessageIdRef.current = null;
      return;
    }
    if (autoMutedForMessageIdRef.current === messageId) {
      return;
    }
    if (!ownPhoneInCall) {
      return;
    }
    autoMutedForMessageIdRef.current = messageId;
    const call = callRef.current;
    if (call == null) {
      return;
    }
    try {
      if (!muted) {
        call.toggleAudioMute?.();
      }
      setMuted(true);
      if (!deafened) {
        call.toggleDeaf?.();
      }
      setDeafened(true);
      setAutoDeafenedForOwnPhone(true);
    } catch (err) {
      console.error("auto-mute on forwarded-phone connect failed", err);
    }
  }, [activeCall, ownPhoneInCall, muted, deafened]);

  // Clear the ringing placeholder once the new leg actually shows up in
  // the participants list (by joined count growth) or after a hard cap.
  const previousParticipantCountRef = useRef(0);
  useEffect(() => {
    if (transferRinging == null) {
      previousParticipantCountRef.current = participants.length;
      return;
    }
    if (participants.length > previousParticipantCountRef.current) {
      setTransferRinging(null);
      if (transferRingingClearTimerRef.current != null) {
        window.clearTimeout(transferRingingClearTimerRef.current);
        transferRingingClearTimerRef.current = null;
      }
    }
    previousParticipantCountRef.current = participants.length;
  }, [participants, transferRinging]);

  const armTransferRingingTimeout = useCallback(() => {
    if (transferRingingClearTimerRef.current != null) {
      window.clearTimeout(transferRingingClearTimerRef.current);
    }
    transferRingingClearTimerRef.current = window.setTimeout(() => {
      setTransferRinging(null);
      transferRingingClearTimerRef.current = null;
    }, 30_000);
  }, []);

  useEffect(
    () => () => {
      if (transferRingingClearTimerRef.current != null) {
        window.clearTimeout(transferRingingClearTimerRef.current);
      }
    },
    [],
  );

  const addAgentToCall = useHandler(
    (targetMongoUserId: MongoUserId, targetLabel: string) => {
      const messageId = activeCall?.messageId;
      if (messageId == null) {
        return;
      }
      previousParticipantCountRef.current = participants.length;
      setTransferRinging({ targetLabel });
      armTransferRingingTimeout();
      addAgentToCallRpc({ messageId, targetMongoUserId }).catch((err) => {
        console.error("addAgentToCall failed", err);
        setTransferRinging(null);
      });
    },
  );

  const addPhoneNumberToCall = useHandler((phoneNumber: string) => {
    const messageId = activeCall?.messageId;
    if (messageId == null) {
      return;
    }
    previousParticipantCountRef.current = participants.length;
    setTransferRinging({ targetLabel: phoneNumber });
    armTransferRingingTimeout();
    addPhoneNumberToCallRpc({ messageId, phoneNumber }).catch((err) => {
      console.error("addPhoneNumberToCall failed", err);
      setTransferRinging(null);
    });
  });

  const value: VoiceV2State = useMemo(
    () => ({
      connectionStatus,
      statusDetail,
      connect,
      connectForMonitoring,
      disconnect,
      desiredOnline,
      micPermission,
      micError,
      requestMic,
      incoming,
      activeCall,
      acceptIncoming,
      rejectIncoming,
      placeOutbound,
      hangup,
      muted,
      deafened,
      deafenedForOwnPhone,
      playbackBlocked,
      resumePlayback,
      onHold,
      recording,
      toggleMute,
      toggleDeafen,
      toggleHold,
      toggleRecord,
      sendDtmf,
      selectMicrophone,
      selectSpeaker,
      availability,
      participants,
      activeSupportTicketId,
      dialerNumber,
      setDialerNumber,
      widgetExpanded,
      setWidgetExpanded,
      isPlacingOutbound,
      transferRinging,
      addAgentToCall,
      addPhoneNumberToCall,
      targetMongoUserId,
      setTargetMongoUserId: handleSetTargetMongoUserId,
      debugTelnyxNotifications,
    }),
    [
      connectionStatus,
      statusDetail,
      connect,
      connectForMonitoring,
      disconnect,
      desiredOnline,
      micPermission,
      micError,
      requestMic,
      incoming,
      activeCall,
      acceptIncoming,
      rejectIncoming,
      placeOutbound,
      hangup,
      muted,
      deafened,
      deafenedForOwnPhone,
      playbackBlocked,
      resumePlayback,
      onHold,
      recording,
      toggleMute,
      toggleDeafen,
      toggleHold,
      toggleRecord,
      sendDtmf,
      selectMicrophone,
      selectSpeaker,
      availability,
      participants,
      activeSupportTicketId,
      dialerNumber,
      widgetExpanded,
      isPlacingOutbound,
      transferRinging,
      addAgentToCall,
      addPhoneNumberToCall,
      targetMongoUserId,
      handleSetTargetMongoUserId,
      debugTelnyxNotifications,
    ],
  );

  return (
    <VoiceContextV2.Provider value={value}>
      {children}
      {/* Plays the remote party; without it calls have no audio. */}
      {remoteStream && <audio autoPlay ref={remoteAudioRef} />}
    </VoiceContextV2.Provider>
  );
});
